/**
 * API Endpoints - Centralized endpoint definitions
 * 
 * Author: Iswe Solutions Team
 * 
 * IMPORTANT: The backend API uses `/api` prefix (NOT `/api/v1`).
 * All endpoints are relative to this base URL.
 * 
 * Example: To call pools dashboard, use `/pools/dashboard`
 * The full URL will be: `/api/pools/dashboard`
 */

/**
 * Base API URL - Backend uses /api prefix (no version number)
 * This is configured in backend/app/main.py
 */
export const API_BASE_URL = '/api';

/**
 * Authentication endpoints
 */
export const AUTH = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  ME: '/auth/me',
} as const;

/**
 * Pool monitoring endpoints
 */
export const POOLS = {
  STATUS: '/pools/status',
  DASHBOARD: '/pools/dashboard',
  DEFINITIONS: '/pools/definitions',
  SCHEDULER_STATUS: '/pools/scheduler/status',
  SOURCES_PROFILES: '/pools/sources/profiles',
  SOURCES_ALLOCATIONS: '/pools/sources/allocations',
  TASKS_ACTIVE: '/pools/tasks/active',
  TASKS_BY_DAG: '/pools/tasks/by-dag',
  DAG_RUNS_RECENT: '/pools/dag-runs/recent',
  EXTRACTION_REALTIME: '/pools/tasks/extraction/realtime',
  TASK_LOGS: '/pools/tasks',  // Appended with /{dag_id}/{run_id}/{task_id}/logs
} as const;

/**
 * Source management endpoints
 */
export const SOURCES = {
  LIST: '/sources/',
  CREATE: '/sources/',
  GET: (id: string) => `/sources/${id}`,
  UPDATE: (id: string) => `/sources/${id}`,
  DELETE: (id: string) => `/sources/${id}`,
  HEALTH: (id: string) => `/sources/${id}/health`,
} as const;

/**
 * Data models endpoints (datasets, programs, trackers)
 */
export const DATA_MODELS = {
  LIST: '/data-models',
  DATASETS: '/data-models/datasets',
  PROGRAMS: '/data-models/programs',
  TRACKERS: '/data-models/trackers',
} as const;

/**
 * Configuration endpoints
 */
export const CONFIG = {
  INFRASTRUCTURE: '/config/infrastructure',
  SYSTEM_SETTINGS: '/config/system-settings',
  SYSTEM_SETTINGS_SCHEMA: '/config/system-settings/schema',
  STORAGE: '/config/storage',
} as const;

/**
 * Dashboard endpoints
 */
export const DASHBOARDS = {
  DATA_QUALITY: '/dashboards/data-quality',
  EXTRACTION: '/dashboards/extraction',
  MONITORING: '/dashboards/monitoring',
  PIPELINE: '/dashboards/pipeline',
  SYSTEM_HEALTH: '/dashboards/system-health',
  CONTAINERS: '/dashboards/containers',
  ERRORS: '/dashboards/errors',
} as const;

/**
 * Analytics endpoints
 */
export const ANALYTICS = {
  SUMMARY: '/analytics/summary',
  TRENDS: '/analytics/trends',
} as const;

/**
 * Airflow integration endpoints
 */
export const AIRFLOW = {
  DAGS: '/airflow/dags',
  DAG_RUNS: '/airflow/dag-runs',
  TASKS: '/airflow/tasks',
  TRIGGER: (dagId: string) => `/airflow/dags/${dagId}/trigger`,
} as const;

/**
 * Health check endpoints
 */
export const HEALTH = {
  STATUS: '/health/status',
  DETAILED: '/health/detailed',
  SOURCES: '/health/sources',
} as const;

/**
 * Container management endpoints
 */
export const CONTAINERS = {
  LIST: '/containers',
  STATUS: '/containers/status',
  RESTART: (name: string) => `/containers/${name}/restart`,
  LOGS: (name: string) => `/containers/${name}/logs`,
} as const;

/**
 * Logs endpoints
 */
export const LOGS = {
  STRUCTURED: '/logs/structured',
  SEARCH: '/logs/search',
} as const;

/**
 * Monitoring endpoints
 */
export const MONITORING = {
  ANOMALIES: '/monitoring/anomalies',
  TREND_ANALYSIS: (poolName: string) => `/monitoring/trend-analysis/${poolName}`,
} as const;

/**
 * Backup endpoints
 */
export const BACKUP = {
  LIST: '/backup/list',
  CREATE: '/backup/create',
  RESTORE: '/backup/restore',
} as const;

/**
 * AI/Iceberg endpoints - Gold layer analytics via Trino
 * These endpoints query the Iceberg Gold layer through the AI query engine
 */
export const AI_ICEBERG = {
  HEALTH: '/ai/iceberg/health',
  TABLES: '/ai/iceberg/tables',
  STATS: '/ai/iceberg/stats',
  QUERY: '/ai/iceberg/query',
  SURVEILLANCE: '/ai/iceberg/surveillance',
  ANOMALIES: '/ai/iceberg/anomalies',
  SCHEMA: '/ai/iceberg/schema',
} as const;

/**
 * AI Query endpoints - Natural language queries
 */
export const AI = {
  QUERY: '/ai/query',
  STATUS: '/ai/status',
  TRAINING: {
    EXAMPLES: '/ai/training/examples',
    STATS: '/ai/training/stats',
    SEED: '/ai/training/seed',
    ADD: '/ai/training/add',
  },
  KNOWLEDGE: '/ai/agent/knowledge',
  CACHE_STATS: '/ai/cache/stats',
  CONVERSATIONS: '/ai/conversations',
  REFRESH_SCHEMA: '/ai/agent/refresh-schema',
  SUGGESTED_QUESTIONS: '/ai/suggested-questions',
} as const;

/**
 * All endpoints grouped
 */
export const ENDPOINTS = {
  AUTH,
  POOLS,
  SOURCES,
  DATA_MODELS,
  CONFIG,
  DASHBOARDS,
  ANALYTICS,
  AIRFLOW,
  HEALTH,
  CONTAINERS,
  LOGS,
  MONITORING,
  BACKUP,
  AI,
  AI_ICEBERG,
} as const;

export default ENDPOINTS;

// Re-export config service for convenient access to infrastructure URLs
export {
  configService,
  useConfig,
  useDeepLink,
  useServiceUrl,
  buildMinioSourceUrl,
  buildMinioDatasetUrl,
  buildAirflowDagUrl,
  buildAirflowDagRunUrl,
} from './configService';
